import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BellIcon,
  BellSlashIcon,
  UserPlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import Avatar from '@/components/Avatar';
import { useAuth } from '@/context/AuthContext';
import type { Conversation } from '@/hooks/useAllConversations';
import useChatGroupActions, { type ChatGroupMemberDetail } from '@/hooks/useChatGroupActions';
import useChatMute from '@/hooks/useChatMute';
import useGroupMembers from '@/hooks/useGroupMembers';

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

interface Props {
  conversation: Conversation;
  onClose: () => void;
  /** Called after leave/archive so the layout can clear the selection. */
  onLeftConversation: () => void;
  onConversationsChanged: () => void;
}

export default function ConversationDetailsPanel({
  conversation,
  onClose,
  onLeftConversation,
  onConversationsChanged,
}: Props) {
  const { user } = useAuth();
  const actions = useChatGroupActions(conversation.groupId);
  const { members: groupMembers } = useGroupMembers(conversation.groupId);
  const { muteStates, toggleMute, fetchMuteStates } = useChatMute();

  const [chatMembers, setChatMembers] = useState<ChatGroupMemberDetail[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isChatGroup = conversation.chatGroupId !== null;
  const muteKey = conversation.chatGroupId ?? 'everyone';
  const isMuted = muteStates[muteKey] || false;
  const isOwner = conversation.isOwner;

  useEffect(() => {
    fetchMuteStates(conversation.groupId);
  }, [conversation.groupId, fetchMuteStates]);

  const loadMembers = useCallback(async () => {
    if (!conversation.chatGroupId) return;
    try {
      setChatMembers(await actions.listMembers(conversation.chatGroupId));
    } catch (e) {
      console.warn('[Details] Member list failed:', e);
    }
  }, [actions, conversation.chatGroupId]);

  useEffect(() => {
    if (isChatGroup) loadMembers();
  }, [isChatGroup, loadMembers]);

  // Members shown: chat group members, or community-group roster for Everyone
  const displayMembers = useMemo(() => {
    if (isChatGroup) {
      return (chatMembers ?? []).map((m) => ({
        id: m.userId,
        name: m.name,
        photo: m.profileImageUrl,
        badge: m.isOwner ? 'owner' : null,
      }));
    }
    return groupMembers.map((m) => ({
      id: m.userId,
      name: m.userName,
      photo: m.profileImageUrl ?? null,
      badge: m.role === 'leader' ? 'leader' : null,
    }));
  }, [isChatGroup, chatMembers, groupMembers]);

  // Members not yet in the chat group (for the add flow)
  const addableMembers = useMemo(() => {
    if (!isChatGroup || !chatMembers) return [];
    const existing = new Set(chatMembers.map((m) => m.userId));
    return groupMembers.filter((m) => !existing.has(m.userId));
  }, [isChatGroup, chatMembers, groupMembers]);

  const canAddMembers =
    isChatGroup && conversation.type === 'group' && (isOwner || !conversation.addMembersRestricted);

  const run = async (fn: () => Promise<void>, message: string) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e: any) {
      setError(e?.response?.data?.message || message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex h-full w-80 shrink-0 flex-col border-l border-flock-gray-200 bg-white">
      <div className="flex items-center justify-between border-b border-flock-gray-200 px-4 py-3">
        <h3 className="text-sm font-semibold text-flock-navy-900">Details</h3>
        <button onClick={onClose} className="p-1 text-flock-navy-400 hover:text-flock-navy-600">
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {error && (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mb-4">
          <div className="text-base font-semibold text-flock-navy-900">{conversation.name}</div>
          <div className="text-xs text-flock-navy-400">
            {conversation.groupName} · {conversation.organizationName}
          </div>
        </div>

        <button
          onClick={() =>
            run(
              () => toggleMute(conversation.groupId, conversation.chatGroupId ?? undefined),
              'Could not update mute setting.'
            )
          }
          disabled={busy}
          className="mb-4 flex w-full items-center gap-2 rounded-lg border border-flock-gray-200 px-3 py-2 text-sm text-flock-navy-700 transition hover:bg-flock-navy-50"
        >
          {isMuted ? <BellIcon className="h-4 w-4" /> : <BellSlashIcon className="h-4 w-4" />}
          {isMuted ? 'Unmute notifications' : 'Mute notifications'}
        </button>

        <div className="mb-1 flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wide text-flock-navy-400">
            Members ({displayMembers.length})
          </span>
          {canAddMembers && addableMembers.length > 0 && (
            <button
              onClick={() => setAdding((v) => !v)}
              className="flex items-center gap-1 text-xs font-medium text-flock-navy-600 hover:underline"
            >
              <UserPlusIcon className="h-4 w-4" /> Add
            </button>
          )}
        </div>

        {adding && (
          <div className="mb-3 rounded-lg border border-flock-gray-200 p-2">
            {addableMembers.map((m) => (
              <button
                key={m.userId}
                disabled={busy}
                onClick={() =>
                  run(async () => {
                    await actions.addMembers(conversation.chatGroupId!, [m.userId]);
                    await loadMembers();
                    onConversationsChanged();
                  }, 'Could not add member.')
                }
                className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-flock-navy-800 hover:bg-flock-navy-50"
              >
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-flock-navy-400 text-[10px] font-semibold text-white">
                  {initials(m.userName)}
                </span>
                {m.userName}
              </button>
            ))}
          </div>
        )}

        <div className="space-y-1">
          {displayMembers.map((m) => (
            <div key={m.id} className="flex items-center gap-3 rounded-lg px-2 py-1.5">
              <Avatar src={m.photo} name={m.name} sizeClass="h-8 w-8" />
              <span className="flex-1 truncate text-sm text-flock-navy-900">{m.name}</span>
              {m.badge && (
                <span className="rounded-full bg-flock-navy-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-flock-navy-600">
                  {m.badge}
                </span>
              )}
              {isChatGroup && isOwner && m.id !== user?.id && (
                <button
                  disabled={busy}
                  onClick={() =>
                    run(async () => {
                      await actions.removeMember(conversation.chatGroupId!, m.id);
                      await loadMembers();
                      onConversationsChanged();
                    }, 'Could not remove member.')
                  }
                  className={clsx('p-1 text-flock-navy-300 hover:text-red-500')}
                  title="Remove from chat"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {isChatGroup && conversation.type === 'group' && (
        <div className="border-t border-flock-gray-200 p-4">
          {isOwner ? (
            <button
              disabled={busy}
              onClick={() => {
                if (!window.confirm('Archive this chat group for everyone?')) return;
                run(async () => {
                  await actions.archiveChatGroup(conversation.chatGroupId!);
                  onConversationsChanged();
                  onLeftConversation();
                }, 'Could not archive chat group.');
              }}
              className="w-full rounded-lg border border-red-200 px-3 py-2 text-sm font-medium text-red-600 transition hover:bg-red-50"
            >
              Archive chat group
            </button>
          ) : (
            <button
              disabled={busy}
              onClick={() => {
                if (!window.confirm('Leave this chat group?')) return;
                run(async () => {
                  await actions.leaveChatGroup(conversation.chatGroupId!);
                  onConversationsChanged();
                  onLeftConversation();
                }, 'Could not leave chat group.');
              }}
              className="w-full rounded-lg border border-red-200 px-3 py-2 text-sm font-medium text-red-600 transition hover:bg-red-50"
            >
              Leave chat group
            </button>
          )}
        </div>
      )}
    </div>
  );
}
